import React, { memo } from "react";
import { useTranslation } from "react-i18next";

import { MediumCircleContainer } from "../../../../components/CircleContainer/styles";
import { FreeTag, PlusTag } from "../../../../components/PlusTag";
import constants from "../../../../constants";
import CalendarIcon from "../../../../icons/Calendar";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import TrashIcon from "../../../../icons/Trash";
import { Site } from "../../../../model/site";
import { Subscription } from "../../../../services/api";
import { SiteThumbnail } from "../SiteThumbnail";
import { Actions, Container, Info, Status, TitleRow } from "./styles";

// "Open in a new tab", drawn like the trash icon (same 16px grid, 18px on screen, filled outline)
export const OpenInNewIcon = ({ color }: { color: string }) => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M9.2 2.6C9.2 2.27 9.47 2 9.8 2H13.4C13.73 2 14 2.27 14 2.6V6.2C14 6.53 13.73 6.8 13.4 6.8C13.07 6.8 12.8 6.53 12.8 6.2V4.05L8.42 8.42C8.19 8.66 7.81 8.66 7.58 8.42C7.34 8.19 7.34 7.81 7.58 7.58L11.95 3.2H9.8C9.47 3.2 9.2 2.93 9.2 2.6ZM4.6 4.4C4.27 4.4 4 4.67 4 5V11.4C4 11.73 4.27 12 4.6 12H11C11.33 12 11.6 11.73 11.6 11.4V9.2C11.6 8.87 11.87 8.6 12.2 8.6C12.53 8.6 12.8 8.87 12.8 9.2V11.4C12.8 12.39 11.99 13.2 11 13.2H4.6C3.61 13.2 2.8 12.39 2.8 11.4V5C2.8 4.01 3.61 3.2 4.6 3.2H6.8C7.13 3.2 7.4 3.47 7.4 3.8C7.4 4.13 7.13 4.4 6.8 4.4H4.6Z"
      fill={color}
    />
  </svg>
);

type Props = {
  site: Site;
  subscription?: Subscription;
  disabled: boolean;
  onEditClick: () => void;
  /** Connected custom domain, if any: shown before the Keepo's address and opened by the launch button. */
  customDomain?: string;
  /** A Keepo being created (never published): a "Rascunho" tag, and a trash button to discard it. */
  draft?: boolean;
  onDiscardClick?: () => void;
  /** Its agenda is on: a shortcut to the bookings, beside the launch button */
  onAgendaClick?: () => void;
};

const CardSite = ({
  site,
  onEditClick,
  disabled,
  subscription = { type: "FREE" },
  customDomain,
  draft,
  onDiscardClick,
  onAgendaClick,
}: Props) => {
  const { t } = useTranslation();
  const keepoUrl = `${site?.url}.${constants.nameSite}`;

  const launchHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) {
      return;
    }
    window.open(`//${customDomain || keepoUrl}`);
  };

  const isPlus = subscription?.type === "PLUS";

  return (
    <Container onClick={onEditClick} title={t("pages.sites.card.details.buttonTitle")}>
      <SiteThumbnail site={site} compact />
      <Info>
        <TitleRow>
          <h4>{site.title}</h4>
          {isPlus ? <PlusTag /> : !draft && <FreeTag>{t("pages.keepoDashboard.plan.free")}</FreeTag>}
        </TitleRow>
        {/* With a custom domain, both addresses share the line (the custom one first) */}
        {draft ? (
          <>
            {site?.url && <p className="url">{keepoUrl}</p>}
            {/* Same as the active/inactive status, in grey */}
            <Status $active={false}>{t("pages.sites.card.draft.tag")}</Status>
          </>
        ) : (
          <>
            <p className="url">{customDomain ? `${customDomain} · ${keepoUrl}` : keepoUrl}</p>
            <Status $active={!disabled}>
              {disabled ? t("pages.keepoDashboard.active.off") : t("pages.keepoDashboard.active.on")}
            </Status>
          </>
        )}
      </Info>
      <Actions>
        {draft ? (
          // A PLUS Keepo not published yet has no discard: it's paid for
          onDiscardClick && (
            <MediumCircleContainer
              className="discard"
              onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                e.stopPropagation();
                onDiscardClick?.();
              }}
              $color="var(--color-neutral-base)"
              cursor="pointer"
              title={t("pages.sites.card.draft.discard")}>
              <TrashIcon color="var(--color-neutral-white)" size={18} />
            </MediumCircleContainer>
          )
        ) : (
          <>
            {onAgendaClick && (
              // a real button: the keyboard reaches it and Enter/Space open it
              <MediumCircleContainer
                as="button"
                type="button"
                className="agenda"
                aria-label={t("pages.sites.card.agenda")}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  onAgendaClick();
                }}
                $color="var(--color-neutral-base)"
                cursor="pointer"
                title={t("pages.sites.card.agenda")}>
                {/* the calendar drawn at the launch icon's size */}
                <span className="flex [&>svg]:h-[18px] [&>svg]:w-[18px]">
                  <CalendarIcon color="var(--color-neutral-white)" />
                </span>
              </MediumCircleContainer>
            )}
            <MediumCircleContainer
              className="launch"
              onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => launchHandler(e)}
              $color={disabled ? "var(--color-neutral-dark)" : "var(--color-neutral-base)"}
              cursor={disabled ? "not-allowed" : "pointer"}
              aria-disabled={disabled}
              title={disabled ? t("pages.sites.card.launch.disabled") : t("pages.sites.card.launch.title")}>
              <OpenInNewIcon color={disabled ? "var(--color-neutral-light)" : "var(--color-neutral-white)"} />
            </MediumCircleContainer>
          </>
        )}
        <span className="chevron">
          <ChevronRightIcon width={14} height={14} color="currentColor" />
        </span>
      </Actions>
    </Container>
  );
};

export default memo(CardSite);
